import { UUID } from "@bitory-gmbh/nidavellir";
import { documentDbPut } from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Query,
  useDocumentDBGetAll,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import {
  useHtzVisualAssessmentCatalogShowActiveCatalogQuery,
  useHtzVisualAssessmentCatalogShowQuery,
  useLazyHtzVisualAssessmentCatalogShowActiveCatalogQuery,
  useLazyHtzVisualAssessmentCatalogShowQuery,
} from "@/services/backend/htz/inspection/api/visual-assessment-catalog-api";
import { VisualAssessmentCatalog } from "@/services/backend/htz/inspection/types/visual-assessment-catalog";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function useVisualAssessmentCatalogOffline(
  workOrderId: UUID,
  companyId: string,
) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [loadActive] =
    useLazyHtzVisualAssessmentCatalogShowActiveCatalogQuery();
  const [loadCatalog] = useLazyHtzVisualAssessmentCatalogShowQuery();

  const prepMsg = t("Speichere Sichtbewertungskatalog ...");
  const doneMsg = t("Sichtbewertungskatalog");

  const prepare = async () => {
    const activeResult = await loadActive({ companyId });
    if (activeResult.error) throw activeResult.error;
    if (!activeResult.data?.catalogId)
      throw new Error("No active catalog found");

    const catalogResult = await loadCatalog({
      id: activeResult.data.catalogId,
    });
    if (catalogResult.error) throw catalogResult.error;

    addStale("visualAssessmentCatalog");
    setSynced((prev) => [...prev, prepMsg]);
    await documentDbPut(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "visualAssessmentCatalog",
      catalogResult.data!,
    );
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  return { prepare };
}

export function useGetVisualAssessmentCatalog(
  workOrderId: string,
  companyId: string,
): Query<VisualAssessmentCatalog> {
  const { offlineState } = useOfflineContext();

  const {
    data: activeCatalog,
    isLoading: activeCatalogIsLoading,
    error: activeCatalogError,
  } = useHtzVisualAssessmentCatalogShowActiveCatalogQuery(
    { companyId },
    { skip: !companyId },
  );

  const {
    data: visualAssessmentCatalog,
    isLoading: visualAssessmentCatalogIsLoading,
    error: visualAssessmentCatalogError,
  } = useHtzVisualAssessmentCatalogShowQuery(
    {
      id: activeCatalog?.catalogId ?? "",
    },
    {
      skip: activeCatalog === undefined || activeCatalog?.catalogId === null,
    },
  );

  const {
    data: indexedDBData,
    isLoading: indexedDBIsLoading,
    error: indexedDBError,
  } = useDocumentDBGetAll<VisualAssessmentCatalog>(
    GSMDocumentKind.WorkOrder,
    workOrderId,
    "visualAssessmentCatalog",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: visualAssessmentCatalog,
        isLoading: activeCatalogIsLoading || visualAssessmentCatalogIsLoading,
        error: activeCatalogError || visualAssessmentCatalogError,
      };
    case OfflineState.OFFLINE:
      return {
        data: indexedDBData ? indexedDBData[0] : undefined,
        isLoading: indexedDBIsLoading,
        error: indexedDBError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: { status: 500 },
      };
  }
}
